import { styled } from '@mui/material/styles';

export type PillTone = 'neutral' | 'positive' | 'warning' | 'negative' | 'info';

const Root = styled('span', {
  shouldForwardProp: (prop) => prop !== 'tone' && prop !== 'dot',
})<{ tone: PillTone; dot: boolean }>(({ theme, tone, dot }) => {
  const { color } = theme.palette.tokens;
  const palette: Record<PillTone, { fg: string; bg: string }> = {
    neutral: { fg: color.textSecondary, bg: color.n100 },
    positive: { fg: color.positive, bg: color.positiveTint },
    warning: { fg: color.warning, bg: color.warningTint },
    negative: { fg: color.negative, bg: color.negativeTint },
    info: { fg: color.textPrimary, bg: color.actionTint },
  };
  const { fg, bg } = palette[tone];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '22px',
    padding: '0 8px',
    borderRadius: `${theme.palette.tokens.radius.pill}px`,
    backgroundColor: bg,
    color: fg,
    fontSize: `${theme.palette.tokens.type.caption.size}px`,
    lineHeight: theme.palette.tokens.type.caption.lineHeight,
    fontWeight: theme.palette.tokens.font.weightMedium,
    letterSpacing: theme.palette.tokens.type.caption.tracking,
    whiteSpace: 'nowrap',
    ...(dot && {
      '&::before': {
        content: '""',
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        backgroundColor: fg,
        flexShrink: 0,
      },
    }),
  };
});

interface StatusPillProps {
  children: string;
  tone?: PillTone;
  dot?: boolean;
  title?: string;
}

/**
 * Status pill (guide 7.6): 22px, tint background, semantic text, no icon, no
 * uppercase. Use it only when the state is NOT the default one — silence is
 * the normal state.
 */
export function StatusPill({
  children,
  tone = 'neutral',
  dot = false,
  title,
}: StatusPillProps): JSX.Element {
  return (
    <Root tone={tone} dot={dot} title={title}>
      {children}
    </Root>
  );
}
